// ─── OFFLINE STORAGE ─────────────────────────────────────────────────────────
// Guarda cálculos localmente y sincroniza cuando hay internet

const STORAGE_KEY = 'adrantaleh_calculos_offline';
const SYNC_QUEUE_KEY = 'adrantaleh_sync_queue';

// Generar ID único local
const localId = () => `local_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;

// ── Leer todos los cálculos guardados localmente ──────────────────────────────
export const getLocalCalculos = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
};

// ── Guardar cálculo localmente ────────────────────────────────────────────────
export const saveLocalCalculo = (calculo) => {
  try {
    const calculos = getLocalCalculos();
    const nuevo = {
      ...calculo,
      id: calculo.id || localId(),
      _local: !calculo.id || calculo.id.startsWith('local_'),
      _pendingSync: true,
      createdAt: calculo.createdAt || new Date().toISOString(),
    };
    calculos.push(nuevo);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(calculos));

    // Agregar a cola de sincronización
    addToSyncQueue({ action: 'create', data: nuevo });
    return nuevo;
  } catch (e) {
    console.error('Error guardando localmente:', e);
    return null;
  }
};

// ── Eliminar cálculo local ────────────────────────────────────────────────────
export const deleteLocalCalculo = (id) => {
  try {
    const calculos = getLocalCalculos().filter(c => c.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(calculos));
    addToSyncQueue({ action: 'delete', id });
  } catch (e) { console.error('Error eliminando localmente:', e); }
};

// ── Obtener cálculos por proyecto ─────────────────────────────────────────────
export const getLocalCalculosByProject = (projectId) => {
  return getLocalCalculos().filter(c => c.projectId === projectId);
};

// ── Reemplazar cálculos de un proyecto (cuando vienen del servidor) ───────────
export const setProjectCalculos = (projectId, calculos) => {
  try {
    const all = getLocalCalculos().filter(c => c.projectId !== projectId);
    const server = calculos.map(c => ({ ...c, _local: false, _pendingSync: false }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...all, ...server]));
  } catch (e) { console.error('Error actualizando locales:', e); }
};

// ── Cola de sincronización ────────────────────────────────────────────────────
const addToSyncQueue = (item) => {
  try {
    const queue = getSyncQueue();
    queue.push({ ...item, timestamp: Date.now() });
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch {}
};

export const getSyncQueue = () => {
  try {
    const data = localStorage.getItem(SYNC_QUEUE_KEY);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
};

export const clearSyncQueue = () => {
  localStorage.removeItem(SYNC_QUEUE_KEY);
};

export const removeSyncItem = (timestamp) => {
  try {
    const queue = getSyncQueue().filter(i => i.timestamp !== timestamp);
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch {}
};

// ── Detectar conexión ─────────────────────────────────────────────────────────
export const isOnline = () => navigator.onLine;

export const onConnectionChange = (callback) => {
  window.addEventListener('online', () => callback(true));
  window.addEventListener('offline', () => callback(false));
  return () => {
    window.removeEventListener('online', () => callback(true));
    window.removeEventListener('offline', () => callback(false));
  };
};

// ── Sincronizar cola pendiente con el servidor ────────────────────────────────
export const syncPendingCalculos = async (apiInstance) => {
  if (!isOnline()) return { synced: 0, failed: 0 };

  const queue = getSyncQueue();
  if (queue.length === 0) return { synced: 0, failed: 0 };

  let synced = 0, failed = 0;

  for (const item of queue) {
    try {
      if (item.action === 'create') {
        const { id, _local, _pendingSync, ...payload } = item.data;
        const res = await apiInstance.post('/calculations', payload);
        // Update local storage with server ID
        const all = getLocalCalculos();
        const idx = all.findIndex(c => c.id === item.data.id);
        if (idx !== -1) {
          all[idx] = { ...res.data, _local: false, _pendingSync: false };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
        }
        removeSyncItem(item.timestamp);
        synced++;
      } else if (item.action === 'delete') {
        if (!item.id.startsWith('local_')) {
          await apiInstance.delete(`/calculations/${item.id}`);
        }
        removeSyncItem(item.timestamp);
        synced++;
      }
    } catch (e) {
      console.warn('Sync failed for item:', item, e.message);
      failed++;
    }
  }

  return { synced, failed };
};
